import React, { useEffect, useState, useCallback } from "react";
import { History, X, KeyRound } from "lucide-react";
import { readHistory, removeEntry } from "../../lib/credentialHistory";

/**
 * CredentialHistoryChips — inline recall of the last 5 credential entries
 * a client used for a given game + credential-type combo. Everything is
 * read from localStorage; picking a chip fires `onSelect(entry)` so the
 * parent form can patch its state.
 *
 * Design language: horizontal pill row, AG7 neon cyan on hover, individual
 * ✕ button per chip. Mobile-friendly (44px tap targets, no scroll trap).
 */
export function CredentialHistoryChips({
  gameId,
  credType = "id",
  onSelect,
  refreshKey = 0,
}) {
  const [entries, setEntries] = useState([]);

  const reload = useCallback(() => {
    setEntries(readHistory(gameId, credType));
  }, [gameId, credType]);

  useEffect(() => {
    reload();
  }, [reload, refreshKey]);

  if (!entries || entries.length === 0) return null;

  const remove = (e, entry) => {
    e.stopPropagation();
    const next = removeEntry(gameId, credType, entry);
    setEntries(next.slice(0, 5));
  };

  const primaryFor = (entry) => {
    // Return a short human label for the chip.
    if (credType === "id_server" && entry.playerServer) {
      return `${entry.playerId} (${entry.playerServer})`;
    }
    return entry.playerId;
  };

  return (
    <div
      data-testid="credential-history-chips"
      className="mt-2 space-y-1.5"
    >
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-cyan-300/70">
        <History className="w-3 h-3" />
        <span>Últimas cuentas usadas</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {entries.map((entry) => {
          const key = `${entry.playerId}|${entry.playerServer || ""}`;
          const label = primaryFor(entry);
          const hasPw = !!entry.playerPassword;
          return (
            <div
              key={key}
              data-testid={`cred-chip-${entry.playerId}`}
              role="button"
              tabIndex={0}
              onClick={() => onSelect && onSelect(entry)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect && onSelect(entry);
                }
              }}
              className="group inline-flex items-center gap-1.5 min-h-[36px] pl-2.5 pr-1 py-1 rounded-full border border-cyan-500/40 bg-cyan-500/[0.06] hover:bg-cyan-500/15 hover:border-cyan-300 text-cyan-100 text-[11px] font-mono cursor-pointer transition-all shadow-[0_0_12px_-6px_rgba(34,211,238,0.55)] hover:shadow-[0_0_18px_-4px_rgba(34,211,238,0.9)] active:scale-95"
            >
              {hasPw && <KeyRound className="w-3 h-3 text-cyan-300 flex-shrink-0" />}
              <span className="truncate max-w-[180px] sm:max-w-none">{label}</span>
              {entry.name && (
                <span className="text-[9px] text-cyan-400/70 hidden sm:inline">· {entry.name}</span>
              )}
              <button
                type="button"
                onClick={(e) => remove(e, entry)}
                data-testid={`cred-chip-remove-${entry.playerId}`}
                aria-label={`Quitar ${label} del historial`}
                className="ml-0.5 w-5 h-5 rounded-full flex items-center justify-center text-cyan-300/60 hover:text-red-300 hover:bg-red-500/20 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CredentialHistoryChips;
